/**
 * MaterialListModal — modal editor for a materials list attached to a Daily Plan.
 *
 * Similar UX to the photo picker: workers open a list, write down materials
 * they need on site, warehouse crew ticks each item as "prepared" once ready.
 *
 * Autosaves title + items with a 500ms debounce (silent on success).
 * Anyone with Doserne access can edit — warehouse just toggles the ✓ checkbox.
 *
 * Props:
 *  - listId (string) — the list to load/edit
 *  - onClose ()
 *  - authHeader () → axios config with Bearer token
 *  - onDeleted (listId) — called after successful delete
 *  - canDelete (bool) — whether admin controls are shown
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2, ClipboardList, Check, Package, Search, Send, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function newLocalItem() {
  return { id: `tmp_${Math.random().toString(36).slice(2, 10)}`, text: "", prepared: false, item_id: "", quantity: 0, unit: "" };
}

export default function MaterialListModal({ listId, onClose, authHeader, onDeleted, canDelete }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [activeId, setActiveId] = useState(null);   // row showing catalog suggestions
  const [reserving, setReserving] = useState(false);
  const [reservedCode, setReservedCode] = useState("");
  // Progress-source lists are locked down to author + admin (enforced
  // server-side). We reflect that in the UI so people don't waste
  // time typing changes that will silently be rejected.
  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyReason, setReadOnlyReason] = useState("");
  const dirtyRef = useRef(false);
  const saveTimer = useRef(null);
  const closingRef = useRef(false);
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserQ, setBrowserQ] = useState("");
  const [browserSys, setBrowserSys] = useState("");
  const [browserQty, setBrowserQty] = useState({});

  // Load list on open
  useEffect(() => {
    if (!listId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/doserne/material-lists/${listId}`, authHeader());
        if (cancelled) return;
        setTitle(data.title || "");
        setItems((data.items || []).length ? data.items : [newLocalItem()]);
        setReservedCode(data.reserved_request_code || "");
        // Probe write permission with a no-op PUT (same title & items).
        // If server responds 403, this is a progress-source list owned
        // by someone else — flip to read-only mode.
        if (data.source === "progress") {
          try {
            await axios.put(
              `${API}/doserne/material-lists/${listId}`,
              { title: data.title, items: data.items || [] },
              authHeader(),
            );
          } catch (probeErr) {
            if (probeErr?.response?.status === 403) {
              setReadOnly(true);
              setReadOnlyReason(
                `Bare ${data.created_by_name || data.created_by || "forfatteren"} og admin kan endre denne listen.`
              );
            }
          }
        }
      } catch (e) {
        if (!cancelled) toast.error("Nu am putut încărca lista");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  // Load the Lager catalog once so rows can auto-link to a real article.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/lager/catalog`, authHeader());
        if (!cancelled) setCatalog(data.items || []);
      } catch { /* Lager not reachable — free-text still works */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced autosave
  const scheduleSave = useCallback(() => {
    if (readOnly) return; // don't waste requests; server would 403
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!dirtyRef.current || closingRef.current) return;
      setSaving(true);
      try {
        const payload = { title: title.trim() || "Listă materiale", items };
        await axios.put(`${API}/doserne/material-lists/${listId}`, payload, authHeader());
        dirtyRef.current = false;
        setSavedAt(new Date());
      } catch (e) {
        if (e?.response?.status === 403) {
          setReadOnly(true);
          setReadOnlyReason("Bare forfatteren og admin kan endre denne listen.");
        } else {
          toast.error("Nu am putut salva lista");
        }
      }
      setSaving(false);
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, items, listId, readOnly]);

  useEffect(() => {
    if (!loading) scheduleSave();
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [title, items, loading, scheduleSave]);

  // Flush on close
  const flushAndClose = async () => {
    closingRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (dirtyRef.current) {
      try {
        await axios.put(`${API}/doserne/material-lists/${listId}`,
          { title: title.trim() || "Listă materiale", items },
          authHeader());
      } catch { /* silent — user is closing anyway */ }
    }
    onClose(title.trim() || "Listă materiale");
  };

  const updateItem = (id, patch) => {
    setItems((cur) => cur.map((it) => it.id === id ? { ...it, ...patch } : it));
  };

  const addItem = () => {
    setItems((cur) => [...cur, newLocalItem()]);
  };

  const removeItem = (id) => {
    setItems((cur) => cur.length <= 1 ? [newLocalItem()] : cur.filter((it) => it.id !== id));
  };

  // Link a row to a real Lager article (autocomplete pick).
  const linkCatalog = (id, cat) => {
    updateItem(id, { text: cat.name, item_id: cat.id, unit: cat.unit || "stk", quantity: 1 });
    setActiveId(null);
  };
  const unlinkCatalog = (id) => updateItem(id, { item_id: "", quantity: 0, unit: "" });
  const setItemQty = (id, q) => updateItem(id, { quantity: Math.max(1, Number(q) || 1) });

  // Add an article straight from the Lager browser pop-out. If it's already
  // in the list we bump the quantity; if there's an empty row we fill it;
  // otherwise we append a new linked row. Keeps the pop-out open for more.
  const addFromCatalog = (cat, qty = 1) => {
    const add = Math.max(1, Number(qty) || 1);
    setItems((cur) => {
      const existingIdx = cur.findIndex((x) => x.item_id === cat.id);
      if (existingIdx >= 0) {
        return cur.map((x, i) => i === existingIdx ? { ...x, quantity: Math.max(1, (Number(x.quantity) || 0) + add) } : x);
      }
      const linked = { text: cat.name, item_id: cat.id, unit: cat.unit || "stk", quantity: add, prepared: false };
      const emptyIdx = cur.findIndex((x) => !((x.text || "").trim()) && !x.item_id);
      if (emptyIdx >= 0) return cur.map((x, i) => i === emptyIdx ? { ...x, ...linked } : x);
      return [...cur, { id: `tmp_${Math.random().toString(36).slice(2, 10)}`, ...linked }];
    });
  };
  const bQty = (id) => Math.max(1, Number(browserQty[id]) || 1);
  const setBQty = (id, v) => setBrowserQty((m) => ({ ...m, [id]: Math.max(1, Number(v) || 1) }));

  const linkedCount = items.filter((i) => i.item_id).length;

  // Convert the linked rows into an OUT request (reserves stock at the depot).
  const reserveToWarehouse = async () => {
    if (!linkedCount || reserving) return;
    setReserving(true);
    try {
      // Flush current edits first so the server sees the latest linked rows.
      await axios.put(`${API}/doserne/material-lists/${listId}`,
        { title: title.trim() || "Listă materiale", items }, authHeader());
      dirtyRef.current = false;
      const { data } = await axios.post(`${API}/doserne/lager/reserve-list/${listId}`, {}, authHeader());
      setReservedCode(data.code || "");
      toast.success(`Rezervat la depozit · ${data.code}`);
      if (Array.isArray(data.warnings) && data.warnings.length) {
        const names = data.warnings.map((w) => `${w.item_name} (cerut ${w.requested}, disponibil ${w.available})`).join(", ");
        toast.warning(`Stoc insuficient în depozit: ${names}`, { duration: 9000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut rezerva la depozit");
    }
    setReserving(false);
  };

  const suggestions = (() => {
    if (!activeId) return [];
    const it = items.find((x) => x.id === activeId);
    if (!it || it.item_id) return [];
    const q = (it.text || "").trim().toLowerCase();
    if (!q) return [];
    return catalog
      .filter((c) => `${c.name} ${c.sku} ${c.art_no} ${c.system}`.toLowerCase().includes(q))
      .slice(0, 6);
  })();

  const browserResults = (() => {
    const q = browserQ.trim().toLowerCase();
    let base = catalog;
    if (browserSys) base = base.filter((c) => (c.system || "").trim() === browserSys);
    if (q) base = base.filter((c) => `${c.name} ${c.sku || ""} ${c.art_no || ""} ${c.system || ""} ${c.category || ""}`.toLowerCase().includes(q));
    return base.slice(0, 60);
  })();
  const browserSystems = Array.from(new Set(catalog.map((c) => (c.system || "").trim()).filter(Boolean))).sort();
  const onKeyDown = (e, idx) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (idx === items.length - 1) {
        addItem();
        // Focus new row on next tick
        setTimeout(() => {
          const inputs = document.querySelectorAll('[data-mlist-item-input]');
          const last = inputs[inputs.length - 1];
          if (last) last.focus();
        }, 30);
      }
    }
  };

  const deleteList = async () => {
    if (!window.confirm("Șterge lista definitiv? Referința rămâne în plan dar linkul nu mai funcționează.")) return;
    try {
      await axios.delete(`${API}/doserne/material-lists/${listId}`, authHeader());
      toast.success("Listă ștearsă");
      onDeleted?.(listId);
      onClose(null);
    } catch { toast.error("Nu am putut șterge"); }
  };

  const preparedCount = items.filter((i) => i.prepared && i.text.trim()).length;
  const totalCount = items.filter((i) => i.text.trim()).length;

  return (
    <>
    <div
      className="fixed inset-0 z-[75] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={flushAndClose}
      data-testid="mlist-backdrop"
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="mlist-modal"
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <ClipboardList className="w-3.5 h-3.5" /> Listă materiale
              {loading ? null : (
                <span className="ml-auto text-emerald-600">
                  {saving ? "…" : savedAt ? "Salvat" : ""}
                </span>
              )}
            </div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Team Klofta — schele"
              readOnly={readOnly}
              className="w-full mt-1 text-lg font-black text-slate-900 bg-transparent border-0 border-b border-transparent focus:border-emerald-400 focus:outline-none px-0"
              data-testid="mlist-title"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              {totalCount > 0 ? `${preparedCount}/${totalCount} pregătit${preparedCount === 1 ? "" : "e"} · ${readOnly ? "kun visning" : "autosalvare"}` : (readOnly ? "kun visning" : "autosalvare")}
            </div>
          </div>
          <button
            onClick={flushAndClose}
            className="text-slate-400 hover:text-slate-900 p-1 ml-2 flex-shrink-0"
            data-testid="mlist-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {readOnly && (
          <div
            className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center gap-2"
            data-testid="mlist-readonly-banner"
          >
            <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{readOnlyReason || "Skrivebeskyttet — kun forfatteren og admin kan endre."}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <ul className="space-y-1.5" data-testid="mlist-items">
              {items.map((it, idx) => (
                <li key={it.id} className={`relative group rounded-lg ${it.prepared ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`mlist-item-${idx}`}>
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => !readOnly && updateItem(it.id, { prepared: !it.prepared })}
                      disabled={readOnly}
                      className={`w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition ${
                        it.prepared
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "border-slate-300 hover:border-emerald-400 bg-white"
                      } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                      title={readOnly ? "Kun forfatteren kan endre" : (it.prepared ? "Pregătit — apasă pentru a debifa" : "Bifează când e pregătit la depozit")}
                      data-testid={`mlist-check-${idx}`}
                    >
                      {it.prepared && <Check className="w-4 h-4" strokeWidth={3} />}
                    </button>

                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <input
                        value={it.text}
                        onChange={(e) => { updateItem(it.id, it.item_id ? { text: e.target.value, item_id: "", quantity: 0, unit: "" } : { text: e.target.value }); setActiveId(it.id); }}
                        onFocus={() => setActiveId(it.id)}
                        onBlur={() => setTimeout(() => setActiveId((cur) => (cur === it.id ? null : cur)), 150)}
                        onKeyDown={(e) => onKeyDown(e, idx)}
                        placeholder={idx === 0 ? "Ex: 20 role plastic — sau caută în Lager…" : "Adaugă articol…"}
                        readOnly={readOnly}
                        className={`flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-sm text-slate-900 py-1 ${it.prepared ? "line-through text-slate-400" : ""}`}
                        data-mlist-item-input="true"
                        data-testid={`mlist-input-${idx}`}
                      />
                      {it.item_id && (
                        <span className="inline-flex items-center gap-1 flex-shrink-0 text-[10px] font-black text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full pl-2 pr-1 py-0.5" title="Legat de catalogul Lager" data-testid={`mlist-linked-${idx}`}>
                          <Package className="w-3 h-3" /> Lager
                          {!readOnly && <button type="button" onClick={() => unlinkCatalog(it.id)} className="hover:text-red-500" title="Deconectează"><X className="w-3 h-3" /></button>}
                        </span>
                      )}
                    </div>

                    {it.item_id && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button type="button" onClick={() => setItemQty(it.id, (it.quantity || 1) - 1)} disabled={readOnly} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center disabled:opacity-40"><Minus className="w-3 h-3" /></button>
                        <input type="number" min={1} value={it.quantity || 1} onChange={(e) => setItemQty(it.id, e.target.value)} readOnly={readOnly} className="w-11 text-center text-sm border border-slate-200 rounded py-0.5 text-slate-900" data-testid={`mlist-qty-${idx}`} />
                        <button type="button" onClick={() => setItemQty(it.id, (it.quantity || 1) + 1)} disabled={readOnly} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center disabled:opacity-40"><Plus className="w-3 h-3" /></button>
                        <span className="text-[10px] text-slate-400 w-6 truncate">{it.unit}</span>
                      </div>
                    )}

                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 p-1 flex-shrink-0"
                        title="Șterge rând"
                        data-testid={`mlist-remove-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {activeId === it.id && !readOnly && suggestions.length > 0 && (
                    <ul className="absolute left-9 right-2 top-full z-10 -mt-0.5 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid={`mlist-suggest-${idx}`}>
                      {suggestions.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => linkCatalog(it.id, c)}
                            className="w-full text-left px-3 py-2 hover:bg-cyan-50 flex items-center gap-2"
                            data-testid={`mlist-suggest-opt-${c.id}`}
                          >
                            <Package className="w-3.5 h-3.5 text-cyan-600 flex-shrink-0" />
                            <span className="flex-1 min-w-0">
                              <span className="text-sm font-bold text-slate-800 truncate block">{c.name}</span>
                              {(c.art_no || c.sku) && <span className="text-[10px] text-slate-400 font-mono">{c.art_no || c.sku}</span>}
                            </span>
                            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded flex-shrink-0 ${c.available < 1 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{c.available} {c.unit}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={addItem}
              disabled={loading || readOnly}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="mlist-add"
            >
              <Plus className="w-4 h-4" /> Adaugă rând
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={() => setShowBrowser(true)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-50 border border-cyan-200 text-sm font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50"
                data-testid="mlist-browse-lager"
              >
                <Package className="w-4 h-4" /> Din Lager
              </button>
            )}
            {!readOnly && linkedCount > 0 && (
              <button
                type="button"
                onClick={reserveToWarehouse}
                disabled={reserving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black text-white"
                data-testid="mlist-reserve"
              >
                {reserving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {reservedCode ? "Rezervă din nou" : `Rezervă la depozit (${linkedCount})`}
              </button>
            )}
            {reservedCode && <span className="text-[10px] font-black text-emerald-600" data-testid="mlist-reserved-code">✓ {reservedCode}</span>}
          </div>

          <div className="flex items-center gap-2">
            {canDelete && !readOnly && (
              <button
                type="button"
                onClick={deleteList}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black text-red-500 hover:text-red-700 px-2 py-1.5"
                data-testid="mlist-delete"
              >
                <Trash2 className="w-3.5 h-3.5" /> Șterge lista
              </button>
            )}
            <button
              type="button"
              onClick={flushAndClose}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
              data-testid="mlist-done"
            >
              Gata
            </button>
          </div>
        </div>
      </div>
    </div>

    {showBrowser && !readOnly && (
      <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowBrowser(false)} data-testid="mlist-browser-backdrop">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mlist-browser">
          <div className="px-4 py-3 border-b border-slate-200 bg-cyan-50 flex items-center gap-2">
            <Package className="w-4 h-4 text-cyan-700" />
            <div className="text-sm font-black text-cyan-800 flex-1">Adaugă din Lager</div>
            <button onClick={() => setShowBrowser(false)} className="text-slate-400 hover:text-slate-900" data-testid="mlist-browser-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input autoFocus value={browserQ} onChange={(e) => setBrowserQ(e.target.value)} placeholder="Caută articol, cod, sistem…" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:border-cyan-500 focus:outline-none" data-testid="mlist-browser-search" />
            </div>
          </div>
          {browserSystems.length > 0 && (
            <div className="px-3 py-2 flex items-center gap-1.5 overflow-x-auto border-b border-slate-100">
              <button type="button" onClick={() => setBrowserSys("")} className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-black border transition ${!browserSys ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`} data-testid="mlist-browser-sys-all">Toate</button>
              {browserSystems.map((s) => (
                <button key={s} type="button" onClick={() => setBrowserSys((cur) => cur === s ? "" : s)} className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-black border transition ${browserSys === s ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`} data-testid={`mlist-browser-sys-${s}`}>{s}</button>
              ))}
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {browserResults.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400" data-testid="mlist-browser-empty">Niciun articol.</div>
            ) : browserResults.map((c) => {
              const inList = items.find((x) => x.item_id === c.id);
              return (
                <div key={c.id} className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-testid={`mlist-browser-opt-${c.id}`}>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-bold text-slate-800 truncate block">{c.name}</span>
                    <span className="text-[10px] text-slate-400">{[c.system, c.category, c.art_no || c.sku].filter(Boolean).join(" · ")}</span>
                  </span>
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded flex-shrink-0 ${c.available < 1 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{c.available} {c.unit}</span>
                  {inList && <span className="text-[10px] font-black text-cyan-700 bg-cyan-100 rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`mlist-browser-inlist-${c.id}`}>×{inList.quantity}</span>}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button type="button" onClick={() => setBQty(c.id, bQty(c.id) - 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100"><Minus className="w-3 h-3" /></button>
                    <input type="number" min={1} value={bQty(c.id)} onChange={(e) => setBQty(c.id, e.target.value)} className="w-9 text-center text-xs border border-slate-200 rounded py-0.5 text-slate-900" data-testid={`mlist-browser-qty-${c.id}`} />
                    <button type="button" onClick={() => setBQty(c.id, bQty(c.id) + 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button type="button" onClick={() => addFromCatalog(c, bQty(c.id))} className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black" data-testid={`mlist-browser-add-${c.id}`}>
                    <Plus className="w-3.5 h-3.5" /> Adaugă
                  </button>
                </div>
              );
            })}
          </div>
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex justify-end">
            <button onClick={() => setShowBrowser(false)} className="px-4 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-semibold" data-testid="mlist-browser-done">Gata</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
